import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

interface PairScreenProps {
  deviceId: string | null;
  code: string;
  label: string;
  state: 'initializing' | 'waiting' | 'negotiating' | 'reconnecting' | 'error';
  errorMessage?: string | null;
}

/**
 * Payload encoded into the QR code. The phone scanner parses this and pairs
 * directly via deviceId, no server-side code lookup needed. Manual entry
 * of the 6-digit code (still rendered next to the QR) remains the fallback.
 *
 * Versioned so we can add fields later (signaling URL, brand) without
 * breaking older phone builds.
 */
function encodeQrPayload(deviceId: string, code: string, label: string): string {
  return JSON.stringify({ v: 1, deviceId, code, label });
}

export function PairScreen({ deviceId, code, label, state, errorMessage }: PairScreenProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!code || !deviceId) return;
    const payload = encodeQrPayload(deviceId, code, label);
    QRCode.toDataURL(payload, {
      errorCorrectionLevel: 'M',
      width: 512,
      margin: 2,
      color: { dark: '#ffffff', light: '#0d0d0f' },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [deviceId, code, label]);

  const statusText =
    state === 'initializing' ? 'Setting up…'
    : state === 'waiting' ? 'Waiting for phone'
    : state === 'negotiating' ? 'Connecting…'
    : state === 'reconnecting' ? 'Reconnecting…'
    : state === 'error' ? (errorMessage ?? 'Something went wrong')
    : '';

  return (
    <div className="pair-screen">
      <div className="pair-header">
        <div className="pair-brand">Yoctogen</div>
        <div className="pair-device">{label}</div>
      </div>

      <div className="pair-body">
        <div className="pair-instruction">
          Open the Yoctogen app on your phone and scan this code, or enter it manually.
        </div>

        <div className="pair-content">
          <div className="pair-qr">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt={`Pair code: ${code}`} />
            ) : (
              <div className="pair-qr-placeholder" />
            )}
          </div>

          <div className="pair-code">
            <div className="pair-code-label">Or enter code</div>
            <div className="pair-code-digits">
              {code.split('').map((d, i) => (
                <span key={i} className="pair-code-digit">{d}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`pair-status pair-status--${state}`}>
        <span className="pair-status-dot" />
        {statusText}
      </div>
    </div>
  );
}
